'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {styled, useTheme, type SxProps, type Theme} from '@mui/material/styles';
import type {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaLocationIcon} from '../icon';
import {Section, StatusTag, Typography} from '../primitives';
import {RegisterInterestButton, TakeTourButton} from './PropertyCtas';

export interface PropertySummaryStatus {
  label: string;
  icon?: ReactNode;
}

export interface PropertySummaryProps {
  name: string;
  location: string;
  locationHref?: string;
  onLocationClick?: () => void;
  logoSrc?: string;
  logoAlt?: string;
  status?: PropertySummaryStatus;
  eyebrow?: ReactNode;
  onRegisterInterest: () => void;
  tour?: {url: string; onClick?: () => void};
  sx?: SxProps<Theme>;
  startingPrice?: number;
}

const Root = styled(Section)(({theme}) => ({
  borderBottom: `1px solid ${theme.palette.colors.border.neutral.default}`,
  [theme.breakpoints.up('md')]: {borderBottomColor: theme.palette.colors.border.neutral.subtle},
}));

const LOGO_SIZE = 100;
const LOGO_SIZE_MOBILE = 50;

const HeaderRow = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {gap: theme.spacing(1.75)},
})) as FC<{children?: ReactNode}>;

const Identity = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  [theme.breakpoints.up('md')]: {gap: theme.spacing(1.5)},
  minWidth: 0,
})) as FC<{children?: ReactNode}>;

const Logo = styled('img')(({theme}) => ({
  width: LOGO_SIZE_MOBILE,
  height: LOGO_SIZE_MOBILE,
  flexShrink: 0,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  [theme.breakpoints.up('md')]: {width: LOGO_SIZE, height: LOGO_SIZE},
}));

const NameBlock = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
})) as FC<{children?: ReactNode}>;

// [MASTER] Details Title Section: the chips sit above the logo row on mobile and
// beside the logo, over the name, on desktop.
const EyebrowRow = styled(Box)(({theme}) => ({
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexWrap: 'wrap',
  [theme.breakpoints.up('md')]: {gap: theme.spacing(1)},
}));

const NameRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
})) as FC<{children?: ReactNode}>;

const LocationRow = styled('a')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.colors.text.primary.default,
  textDecoration: 'none',
  '&[href]:hover': {color: theme.palette.colors.text.primary.hover},
}));

const Actions = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    '& > *': {flex: 1},
  },
})) as FC<{children?: ReactNode}>;

export function PropertySummary({
  name,
  location,
  locationHref,
  onLocationClick,
  logoSrc,
  logoAlt,
  status,
  eyebrow,
  onRegisterInterest,
  tour,
  sx,
  startingPrice,
}: PropertySummaryProps) {
  const {t} = useTranslation();
  const theme = useTheme();
  return (
    <Root tone="subtle" sx={sx}>
      <Container>
        <HeaderRow>
          {eyebrow && <EyebrowRow sx={{display: {xs: 'flex', md: 'none'}, width: '100%', mb: -1}}>{eyebrow}</EyebrowRow>}
          <Identity>
            {logoSrc && <Logo src={logoSrc} alt={logoAlt ?? name} />}
            <NameBlock>
              {eyebrow && <EyebrowRow sx={{display: {xs: 'none', md: 'flex'}}}>{eyebrow}</EyebrowRow>}
              <NameRow>
                <Typography
                  size="title-3"
                  weight="bold"
                  mobileSize="title-5"
                  mobileWeight="bold"
                  as="h1"
                  sx={{textTransform: 'capitalize'}}
                >
                  {name}
                </Typography>
                {status && <StatusTag icon={status.icon}>{status.label}</StatusTag>}
              </NameRow>
              <LocationRow
                as={locationHref ? 'a' : 'div'}
                href={locationHref}
                target={locationHref ? '_blank' : undefined}
                rel={locationHref ? 'noopener noreferrer' : undefined}
                aria-label={locationHref ? `${location} — ${t('view_on_google_maps')}` : undefined}
                onClick={locationHref ? onLocationClick : undefined}
              >
                <AppIcon icon={FaLocationIcon} sx={{fontSize: '0.875em', color: theme => theme.palette.colors.icon.primary.default}} />
                <Typography size="body-2" weight="medium" family="primary" as="span" sx={{letterSpacing: '-0.015em', color: 'inherit'}}>
                  {location}
                </Typography>
              </LocationRow>
              {startingPrice && (
                <Box
                  sx={{
                    backgroundColor: theme.palette.colors.surface.neutral.disabled,
                    color: theme.palette.colors.text.neutral.default,
                    p: theme.spacing(1.25),
                    borderRadius: `${theme.radii.sm}px`,
                  }}
                >
                  <Typography size="body-2" weight="regular" as="span">
                    {t('starting_price')}{' '}
                  </Typography>
                  <Typography size="body-2" weight="semiBold" as="span" sx={{textTransform: 'capitalize'}}>
                    EGP {startingPrice.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </NameBlock>
          </Identity>
          <Actions>
            <RegisterInterestButton onClick={onRegisterInterest} />
            {tour && <TakeTourButton href={tour.url} onClick={tour.onClick} />}
          </Actions>
        </HeaderRow>
      </Container>
    </Root>
  );
}

PropertySummary.displayName = 'PropertySummary';
